import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Badge,
    Button,
    FormLayout,
    HeaderActions,
    Heading,
    LoadingProgress,
    Modal,
    Panel,
    SortableTable,
    Switch,
    Text,
} from "@/ui";
import { Icon } from "@/components";
import { useMutation } from "@apollo/react-hooks";
import {
    MUTATION_UPDATE_TREEZ_APP,
    QUERY_LIST_STORE_APPS,
} from "../../apollo/module-operations/app";
import _ from "lodash";
import { useForm, useFormActions, useMeta, useNotification } from "@/hooks";
import AccountTreezForm from "./AccountTreezForm";
import {
    PlusIcon,
    RowActionsMenu,
    legacyColorToBadge,
    storeTableUiStyles,
} from "./shared";
import { TREEZ_AUTH_MODE_OPTIONS } from "../../config/constant";
import styles from "./GeneralsForm.module.scss";
import deliveryStyles from "./DeliverySchedules.module.scss";

const REORDER_DEBOUNCE_MS = 700;

const SaveIcon = () => <Icon icon="save" />;

const TREEZ_AUTH_MODE_UI = Object.fromEntries(
    TREEZ_AUTH_MODE_OPTIONS.map(option => [
        option.value,
        {
            label: option.label,
            variant: option.value === "PARTNER_CERT" ? "secondary" : "neutral",
        },
    ]),
);

const getTreezAuthMode = value =>
    TREEZ_AUTH_MODE_UI[value] ?? TREEZ_AUTH_MODE_UI.PARTNER_CERT;

const stripRowMeta = row => _.omit(row, "_index");

const getCredentialRowId = row => {
    if (row.store_id) return String(row.store_id);
    return `credential-${row._index}`;
};

const TreezForm = ({ app, loading: pageLoading }) => {
    const notify = useNotification();
    const { user } = useMeta();
    const [showForm, setShowForm] = useState(false);
    const [isCreate, setIsCreate] = useState(true);
    const [rowSelect, setRowSelect] = useState(null);
    const [credentials, setCredentials] = useState([]);
    const [isUpdating, setIsUpdating] = useState(false);
    const [isDelete, setIsDelete] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showSandboxConfirm, setShowSandboxConfirm] = useState(false);
    const isReorderSaveRef = useRef(false);
    const reorderTimerRef = useRef(null);

    const { inputs, setInputs } = useForm();

    if (!_.get(inputs, "id", null)) {
        setInputs(app);
    }

    useEffect(
        () => () => {
            if (reorderTimerRef.current) {
                clearTimeout(reorderTimerRef.current);
            }
        },
        [],
    );

    const [mutate] = useMutation(MUTATION_UPDATE_TREEZ_APP, {
        refetchQueries: [],
        onCompleted: () => {
            if (isReorderSaveRef.current) {
                isReorderSaveRef.current = false;
                setIsUpdating(false);
                notify("Store order updated successfully", "success");
                return;
            }

            handleCancel();
            setIsUpdating(false);
            setIsDelete(false);
            setShowDeleteConfirm(false);
            setInputs({ ...inputs, credentials });
            notify(
                `Credential Treez Was ${
                    isCreate ? "Created" : !isDelete ? "Updated" : "Removed"
                } Successfully"`,
                "success",
            );
        },
        onError: () => {
            if (isReorderSaveRef.current) {
                isReorderSaveRef.current = false;
                setIsUpdating(false);
                notify(
                    "Error updating store order. If the error persists please contact support.",
                    "error",
                );
                return;
            }

            setIsUpdating(false);
            notify(
                `Error ${
                    isCreate ? "Updating" : !isDelete ? "Creating" : "Removing"
                }  The Credential, If The Error Persist Please Contact Support.`,
                "error",
            );
        },
    });

    let options = {
        variables: {
            account_id: user.accountId,
            store_id: user.storeId,
            input: {
                ...inputs,
                type: "Treez",
            },
        },
    };

    const { createOrUpdate, loading } = useFormActions(
        "id",
        "Treez",
        "",
        MUTATION_UPDATE_TREEZ_APP,
        [
            {
                query: QUERY_LIST_STORE_APPS,
                variables: {
                    account_id: user.accountId,
                    store_id: user.storeId,
                },
                skip: user.loading || !user.accountId || !user.storeId,
            },
        ],
        options,
        "",
        () => true,
        () => true,
        "primary",
    );

    const _credentials = (inputs && inputs.credentials) || [];

    const rows = useMemo(
        () =>
            _credentials.map((credential, index) => ({
                ...credential,
                _index: index,
            })),
        [_credentials],
    );

    const columns = useMemo(
        () => [
            {
                id: "actions",
                header: "",
                width: 52,
                align: "center",
                renderCell: row => (
                    <span
                        className={storeTableUiStyles.rowActions}
                        onClick={event => event.stopPropagation()}
                    >
                        <RowActionsMenu
                            disabled={isUpdating || showForm}
                            label={
                                row.store_name ||
                                row.store_id ||
                                `credential ${row._index + 1}`
                            }
                            onDelete={() => {
                                setIsDelete(true);
                                setIsCreate(false);
                                setRowSelect(row._index);
                                setShowDeleteConfirm(true);
                            }}
                            onUpdate={() => {
                                setIsCreate(false);
                                setRowSelect(row._index);
                                setShowForm(true);
                            }}
                        />
                    </span>
                ),
            },
            {
                id: "active",
                header: "Active",
                minWidth: 100,
                renderCell: row => (
                    <Badge
                        variant={legacyColorToBadge(
                            row.active ? "success" : "error",
                        )}
                    >
                        {row.active ? "Yes" : "No"}
                    </Badge>
                ),
            },
            {
                id: "default_store",
                header: "Default",
                minWidth: 100,
                renderCell: row => (
                    <Badge
                        variant={legacyColorToBadge(
                            row.default_store ? "success" : "error",
                        )}
                    >
                        {row.default_store ? "Yes" : "No"}
                    </Badge>
                ),
            },
            {
                id: "store_id",
                header: "Store Id",
                minWidth: 160,
                renderCell: row =>
                    row.store_id ? (
                        <Badge variant="primary">{row.store_id}</Badge>
                    ) : (
                        ""
                    ),
            },
            {
                id: "store_name",
                header: "Store Name",
                minWidth: 180,
                renderCell: row => row.store_name || "",
            },
            {
                id: "organization_id",
                header: "Organization Id",
                minWidth: 180,
                renderCell: row =>
                    row.organization_id ? (
                        <Badge variant="primary">{row.organization_id}</Badge>
                    ) : (
                        <Text tone="secondary" variant="body-sm">
                            —
                        </Text>
                    ),
            },
            {
                id: "auth_mode",
                header: "Auth Mode",
                minWidth: 160,
                renderCell: row => {
                    const { label, variant } = getTreezAuthMode(row.auth_mode);
                    return <Badge variant={variant}>{label}</Badge>;
                },
            },
            {
                id: "client_id",
                header: "Client Id",
                minWidth: 200,
                renderCell: row => row.client_id || "",
            },
            {
                id: "api_key",
                header: "Api Key",
                minWidth: 200,
                renderCell: row => row.api_key || "",
            },
            {
                id: "headless_client_id",
                header: "Headless Client Id",
                minWidth: 200,
                renderCell: row => row.headless_client_id || "",
            },
            {
                id: "headless_client_secret",
                header: "Headless Client Secret",
                minWidth: 200,
                renderCell: row => row.headless_client_secret || "",
            },
        ],
        [isUpdating, showForm],
    );

    const handleReorder = nextRows => {
        const nextCredentials = nextRows.map(stripRowMeta);
        const nextInputs = {
            ...inputs,
            credentials: nextCredentials,
        };
        setInputs(nextInputs);

        if (reorderTimerRef.current) {
            clearTimeout(reorderTimerRef.current);
        }
        reorderTimerRef.current = setTimeout(() => {
            isReorderSaveRef.current = true;
            setIsUpdating(true);
            mutate({
                variables: {
                    account_id: user.accountId,
                    store_id: user.storeId,
                    input: {
                        ...nextInputs,
                        automatic_approval: true,
                        credentials: nextCredentials,
                    },
                },
            });
        }, REORDER_DEBOUNCE_MS);
    };

    const handlerUpdatingOrCreate = array => {
        if (reorderTimerRef.current) {
            clearTimeout(reorderTimerRef.current);
            reorderTimerRef.current = null;
        }
        isReorderSaveRef.current = false;
        setIsUpdating(true);
        setCredentials(array);
        mutate({
            variables: {
                account_id: user.accountId,
                store_id: user.storeId,
                input: {
                    ...inputs,
                    automatic_approval: true,
                    credentials: array,
                },
            },
        });
    };

    const handleCancel = () => {
        setIsUpdating(false);
        setShowForm(false);
    };

    const confirmDelete = () => {
        if (reorderTimerRef.current) {
            clearTimeout(reorderTimerRef.current);
            reorderTimerRef.current = null;
        }
        isReorderSaveRef.current = false;
        const nextCredentials = _.filter(
            inputs.credentials,
            (i, index) => index !== rowSelect,
        );
        setCredentials(nextCredentials);
        setIsUpdating(true);
        mutate({
            variables: {
                account_id: user.accountId,
                store_id: user.storeId,
                input: {
                    ...inputs,
                    credentials: nextCredentials,
                },
            },
        });
    };

    const confirmSandbox = () => {
        setInputs({
            ...inputs,
            sandbox: true,
        });
        setShowSandboxConfirm(false);
    };

    const handleSandboxChange = e => {
        const isChecked = e.target.checked;
        const currentSandboxValue = inputs.sandbox || false;

        if (isChecked && !currentSandboxValue) {
            setShowSandboxConfirm(true);
        } else {
            setInputs({
                ...inputs,
                sandbox: isChecked,
            });
        }
    };

    return (
        <>
            <Modal
                description="Are You Sure You Want To Remove The Credential ?"
                footer={
                    <div className={deliveryStyles.modalActions}>
                        <Button
                            loading={isUpdating}
                            onClick={confirmDelete}
                            type="button"
                        >
                            Confirm
                        </Button>
                        <Button
                            disabled={isUpdating}
                            onClick={() => {
                                setShowDeleteConfirm(false);
                                setIsDelete(false);
                            }}
                            type="button"
                            variant="secondary"
                        >
                            Cancel
                        </Button>
                    </div>
                }
                onOpenChange={open => {
                    if (!open) {
                        setShowDeleteConfirm(false);
                        setIsDelete(false);
                    }
                }}
                open={showDeleteConfirm}
                title="Remove credential"
            >
                {null}
            </Modal>

            <Modal
                description="STOP! Sandbox mode is for testing only. Enabling this will block all live orders from reaching your POS."
                footer={
                    <div className={deliveryStyles.modalActions}>
                        <Button onClick={confirmSandbox} type="button">
                            Confirm
                        </Button>
                        <Button
                            onClick={() => setShowSandboxConfirm(false)}
                            type="button"
                            variant="secondary"
                        >
                            Cancel
                        </Button>
                    </div>
                }
                onOpenChange={open => {
                    if (!open) setShowSandboxConfirm(false);
                }}
                open={showSandboxConfirm}
                title="Activate Sandbox Mode"
            >
                {null}
            </Modal>

            <Panel
                className={styles.root}
                header={
                    <div className={styles.headerBar}>
                        <div className={styles.headerText}>
                            <Heading level={4}>Treez</Heading>
                            <Text tone="secondary" variant="body-sm">
                                Treez Information
                            </Text>
                        </div>
                        <HeaderActions
                            actions={[
                                {
                                    id: "update",
                                    label: "Update",
                                    icon: loading ? undefined : <SaveIcon />,
                                    loading,
                                    disabled: loading || isUpdating,
                                    onClick: () => createOrUpdate(),
                                },
                                {
                                    id: "add-credential",
                                    label: "Add Credential",
                                    icon: <PlusIcon />,
                                    variant: "secondary",
                                    disabled: isUpdating || showForm,
                                    onClick: () => {
                                        setIsCreate(true);
                                        setShowForm(true);
                                    },
                                },
                            ]}
                            aria-label="Treez actions"
                        />
                    </div>
                }
                showHeaderDivider
            >
                {user.loading || pageLoading ? (
                    <div className={styles.loading}>
                        <LoadingProgress
                            indeterminate
                            indicator="circular"
                            size="sm"
                            title="Loading"
                        />
                    </div>
                ) : (
                    <FormLayout>
                        <FormLayout.Section>
                            <Switch
                                checked={Boolean(inputs.sandbox)}
                                label="Sandbox"
                                name="sandbox"
                                onChange={handleSandboxChange}
                            />
                        </FormLayout.Section>

                        {showForm ? (
                            <AccountTreezForm
                                app={inputs}
                                credentials={credentials}
                                handleCancel={handleCancel}
                                handlerUpdatingOrCreate={
                                    handlerUpdatingOrCreate
                                }
                                isCreate={isCreate}
                                isUpdating={isUpdating}
                                selectCredential={rowSelect}
                                setCredentials={setCredentials}
                            />
                        ) : null}

                        <FormLayout.Section title="Credentials">
                            <Text tone="secondary" variant="body-sm">
                                Drag rows to set the store selector display
                                order. Order saves automatically.
                            </Text>
                            <SortableTable
                                columns={columns}
                                disabled={isUpdating || showForm}
                                getRowId={getCredentialRowId}
                                onReorder={handleReorder}
                                rows={rows}
                            />
                        </FormLayout.Section>
                    </FormLayout>
                )}
            </Panel>
        </>
    );
};

export default TreezForm;
